/**
 * @module @eoln/resilience/errors
 * @description Custom error types for resilience patterns
 */

import { ApplicationError } from "@eoln/errors";

/**
 * Thrown when service is unavailable after retries
 */
export class ServiceUnavailableError extends ApplicationError {
  constructor(service: string, attempts: number) {
    super(`Service ${service} unavailable after ${attempts} attempts`, "SERVICE_UNAVAILABLE");
    this.name = "ServiceUnavailableError";
  }
}

/**
 * Thrown when retry limit is exceeded
 */
export class RetryExhaustedError extends ApplicationError {
  constructor(attempts: number, lastError?: Error) {
    super(`All ${attempts} retry attempts exhausted${lastError ? `: ${lastError.message}` : ""}`, "RETRY_EXHAUSTED");
    this.name = "RetryExhaustedError";
  }
}

/**
 * Auth service specific errors
 */
export class AuthServiceUnavailableError extends ApplicationError {
  constructor(message: string) {
    super(message, "AUTH_SERVICE_UNAVAILABLE");
    this.name = "AuthServiceUnavailableError";
  }
}

export class AuthValidationError extends ApplicationError {
  constructor(message: string) {
    super(message, "AUTH_VALIDATION_ERROR");
    this.name = "AuthValidationError";
  }
}

/**
 * Network connectivity errors (DNS, connection refused, etc)
 */
export class NetworkError extends ApplicationError {
  readonly originalError?: Error;

  constructor(message: string, originalError?: Error) {
    super(message, "NETWORK_ERROR");
    this.name = "NetworkError";
    this.originalError = originalError;
  }
}

/**
 * Request timeout errors
 */
export class TimeoutError extends ApplicationError {
  constructor(message: string) {
    super(message, "TIMEOUT_ERROR");
    this.name = "TimeoutError";
  }
}

/**
 * A request was aborted because it exceeded its own deadline.
 *
 * Distinct from NetworkError on purpose: a connection that never opened has no
 * upstream side effect, but a request that TIMED OUT may still be executing on
 * the server. Re-sending it duplicates whatever it was doing -- a slow
 * provisionTenant ran twice because the timeout was raised as a NetworkError,
 * which retry logic treats as safe to replay.
 *
 * Never retry this. Reconcile instead.
 */
export class RequestTimeoutError extends ApplicationError {
  constructor(message: string) {
    super(message, "REQUEST_TIMEOUT");
    this.name = "RequestTimeoutError";
  }
}

/**
 * HTTP 4xx client errors (400-499)
 * These should NOT be retried
 */
export class HttpClientError extends ApplicationError {
  readonly statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message, "HTTP_CLIENT_ERROR");
    this.name = "HttpClientError";
    this.statusCode = statusCode;
  }
}

/**
 * HTTP 5xx server errors (500-599)
 * These MAY be retried
 */
export class HttpServerError extends ApplicationError {
  readonly statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message, "HTTP_SERVER_ERROR");
    this.name = "HttpServerError";
    this.statusCode = statusCode;
  }
}
